import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Collapse, Typography } from '@material-ui/core';

// Read-only "Show the math" panel. Renders the enriched calculation trace
// (Tier-1 quantities + Tier-2 per-line cost) as a collapsible ledger grouped by
// area. Purely presentational — no edits, no recompute.
const useStyles = makeStyles(() => ({
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    userSelect: 'none',
    marginTop: 12,
    fontSize: 12,
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  caret: {
    fontSize: 11,
    color: '#999',
  },
  areaGroup: {
    marginTop: 8,
  },
  areaLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#444',
    padding: '4px 8px',
    background: '#f5f5f5',
    borderRadius: 4,
  },
  line: {
    padding: '5px 8px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.74rem',
    color: '#555',
  },
  lineHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  service: {
    fontWeight: 600,
    color: '#333',
  },
  badge: {
    fontSize: 9,
    fontWeight: 700,
    color: '#fff',
    borderRadius: 8,
    padding: '1px 6px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    letterSpacing: 0.3,
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px 12px',
    marginTop: 3,
    color: '#777',
  },
  cost: {
    fontWeight: 700,
    color: '#222',
  },
  subFormula: {
    marginTop: 3,
    fontSize: '0.7rem',
    color: '#6b6b6b',
    fontStyle: 'italic',
  },
  subLines: {
    marginTop: 4,
    paddingLeft: 12,
    borderLeft: '2px solid #eee',
  },
  subLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.7rem',
    color: '#777',
    padding: '2px 0',
  },
  subLineLabel: {
    color: '#555',
  },
}));

const fmtQty = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtHrs = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtUSD = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

// default → muted/amber; benchmark/measured → green.
const badgeColor = (source) => (source === 'benchmark' || source === 'measured' ? '#4caf50' : '#c99a2e');

const CalcTracePanel = ({ trace }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  if (!trace?.length) return null;

  // Group lines by areaLabel, preserving first-seen order.
  const groups = [];
  const byLabel = new Map();
  trace.forEach((line) => {
    const key = line.areaLabel || '(unlabeled area)';
    let g = byLabel.get(key);
    if (!g) {
      g = { label: key, lines: [] };
      byLabel.set(key, g);
      groups.push(g);
    }
    g.lines.push(line);
  });

  return (
    <Box>
      <div
        className={classes.toggle}
        onClick={() => setOpen((o) => !o)}
        role="button"
        aria-expanded={open}
        tabIndex={0}
      >
        <span className={classes.caret}>{open ? '▾' : '▸'}</span>
        Show the math
      </div>

      <Collapse in={open}>
        {groups.map((g) => (
          <div key={g.label} className={classes.areaGroup}>
            <Typography className={classes.areaLabel}>{g.label}</Typography>
            {g.lines.map((line, i) => {
              const prov = line.provenance || {};
              return (
                <div key={`${line.areaId}-${line.service}-${i}`} className={classes.line}>
                  <div className={classes.lineHead}>
                    <span className={classes.service}>{line.service}</span>
                    <span
                      className={classes.badge}
                      style={{ background: badgeColor(prov.source) }}
                    >
                      {prov.source || 'default'}
                    </span>
                  </div>
                  <div className={classes.meta}>
                    <span>
                      {fmtQty(line.rawQuantity)} → {fmtQty(line.effectiveQuantity)} {line.effectiveUnit}
                    </span>
                    <span>rate {fmtQty(line.productionRate)}</span>
                    {line.modifiersApplied && line.modifiersApplied.length > 0 && (
                      <span>{line.modifiersApplied.join(', ')}</span>
                    )}
                    <span>{fmtHrs(line.annualHours)} hrs/yr</span>
                    <span className={classes.cost}>${fmtUSD(line.annualCost)}</span>
                  </div>
                  {/* Subcontractor math (vendor · qty × rate × visits/yr). */}
                  {line.subFormula && (
                    <div className={classes.subFormula}>{line.subFormula}</div>
                  )}
                  {/* Per-task breakdown (Mowing/Trimming/Blowing…) under the role total. */}
                  {line.subLines && line.subLines.length > 0 && (
                    <div className={classes.subLines}>
                      {line.subLines.map((sub, si) => (
                        <div key={`${sub.label}-${si}`} className={classes.subLine}>
                          <span className={classes.subLineLabel}>{sub.label}</span>
                          <span>{fmtHrs(sub.annualHours)} hrs/yr · ${fmtUSD(sub.annualCost)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </Collapse>
    </Box>
  );
};

export default CalcTracePanel;
